import { breakpoints, Button, Inline, Stack } from '@etg/wings';
import { DangerousHtmlContent } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { price } from '@eti/utils';
import { useEffect } from 'react';
import Media from 'react-media';
import type { TripPriceChange } from '@eti/schema-types';
import { paymentMethodCodes } from '../../constants/paymentMethods';
import { usePaymentMethod } from '../../payment-methods/context/PaymentMethodsContext';
import { klarnaCodes } from '../../payment-methods/klarna/constants';

const { PAYPAL, PAYPAL_PAY_LATER } = paymentMethodCodes;

const summaryRowStyles = css`
  display: flex;
  justify-content: space-between;
`;

const summaryPriceStyles = css`
  align-self: flex-end;
  text-align: end;
`;

const summaryRowNewPriceStyles = css`
  font-size: 1rem;
  font-weight: 700;
`;

const getTotalPriceFor = (
  key: 'priceDiff' | 'priceOld' | 'priceNew',
  priceChanges: TripPriceChange[],
) => priceChanges.reduce((acc, priceChange) => acc + (priceChange[key]?.price?.value ?? 0), 0);

type SelectedPaymentMethod = {
  code: string;
  type: string;
  id: string;
};

type PriceChangeContentProps = {
  isPayInternal?: boolean;
  isPriceIncrease: boolean;
  onAcceptPriceChange: (cb?: () => void) => void;
  onDeclinePriceChange: () => void;
  priceChanges: TripPriceChange[];
  selectedPaymentMethod: SelectedPaymentMethod | null;
  populatePriceChangeDisplayed: (priceChangeText: string) => void;
};

const PriceChangeContent = ({
  isPayInternal,
  isPriceIncrease,
  onAcceptPriceChange,
  onDeclinePriceChange,
  priceChanges,
  selectedPaymentMethod,
  populatePriceChangeDisplayed,
}: PriceChangeContentProps) => {
  const { t } = useTranslation();
  const { onSubmit } = usePaymentMethod();
  const priceDifference = getTotalPriceFor('priceDiff', priceChanges);
  const oldTotalPrice = getTotalPriceFor('priceOld', priceChanges);
  const newTotalPrice = getTotalPriceFor('priceNew', priceChanges);
  const messageTextKey = isPriceIncrease
    ? 'Payment.PriceChangeDialog.Increase.Message'
    : 'Payment.PriceChangeDialog.Decrease.Message';
  const isPaypalSelected =
    selectedPaymentMethod?.code === PAYPAL || selectedPaymentMethod?.code === PAYPAL_PAY_LATER;
  const isKlarnaSelected =
    selectedPaymentMethod && klarnaCodes.includes(selectedPaymentMethod?.code);

  const content = t(
    messageTextKey,
    price.formatPrice(Math.abs(priceDifference)),
    price.formatPrice(newTotalPrice),
  );

  useEffect(() => {
    populatePriceChangeDisplayed(content);
  }, [content, populatePriceChangeDisplayed]);

  const shouldShowAcceptButton = !isPayInternal && !isPaypalSelected && !isKlarnaSelected;

  const handleOnAccept = () => {
    onAcceptPriceChange(onSubmit);
  };

  const handleOnDecline = () => {
    onDeclinePriceChange();
  };

  const buttons = [
    shouldShowAcceptButton && (
      <Button
        data-testid="trip-change-accept-button"
        key="trip-change-accept-button"
        onClick={handleOnAccept}
        variant="primary"
      >
        {t(
          isPriceIncrease
            ? 'Payment.PriceChangeDialog.Increase.Accept'
            : 'Payment.PriceChangeDialog.Decrease.Accept',
        )}
      </Button>
    ),
    <Button
      data-testid="trip-change-decline-button"
      key="trip-change-decline-button"
      onClick={handleOnDecline}
      variant="secondary"
    >
      {t('Payment.PriceChangeDialog.Decline')}
    </Button>,
  ];

  return (
    <Stack spacing={24}>
      <Stack>
        <DangerousHtmlContent
          component="p"
          content={t(
            messageTextKey,
            `<span data-testid="paymentPage-priceChangeDialog-message-priceDiff">${price.formatPrice(
              Math.abs(priceDifference),
            )}</span>`,
            `<span data-testid="paymentPage-priceChangeDialog-message-newPrice">${price.formatPrice(
              newTotalPrice,
            )}</span>`,
          )}
          dataTestId="paymentPage-priceChangeDialog-message"
        />

        {isPayInternal ? (
          <DangerousHtmlContent
            component="p"
            content={t('Payment.ZeroAmountCart.PriceChange.Information')}
            dataTestId="payment-tripChangeModal-InternalContent"
          />
        ) : (
          <Stack
            data-testid={
              isPriceIncrease
                ? 'paymentPage-priceChangeDialog-increase-summary'
                : 'paymentPage-priceChangeDialog-decrease-summary'
            }
            spacing={4}
          >
            <div
              className={summaryRowStyles}
              data-testid="paymentPage-priceChangeDialog-summary-oldPrice"
            >
              <span>{t('Payment.PriceChangeDialog.OldPrice')}</span>
              <span className={summaryPriceStyles}>{price.formatPrice(oldTotalPrice)}</span>
            </div>
            <div
              className={summaryRowStyles}
              data-testid="paymentPage-priceChangeDialog-summary-priceDiff"
            >
              <span>{t('Payment.PriceChangeDialog.PriceDifference')}</span>
              <span className={summaryPriceStyles}>
                {price.formatPrice(Math.abs(priceDifference))}
              </span>
            </div>
            <div
              className={cx(summaryRowStyles, summaryRowNewPriceStyles)}
              data-testid="paymentPage-priceChangeDialog-summary-newPrice"
            >
              <span>{t('Payment.PriceChangeDialog.NewPrice')}</span>
              <span className={summaryPriceStyles}>{price.formatPrice(newTotalPrice)}</span>
            </div>
          </Stack>
        )}
      </Stack>
      <Inline align="end" collapseBelow={breakpoints._560}>
        <Media query={`(min-width: ${breakpoints._560})`}>
          {(matches) => (matches ? buttons.reverse() : buttons)}
        </Media>
      </Inline>
    </Stack>
  );
};

export default PriceChangeContent;
